import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, FileText, Lock, ShieldCheck } from 'lucide-react';
import { Button } from './Button';
import { RichTextEditor } from './RichTextEditor';
import { GespreksverslagAiPaneel } from './GespreksverslagAiPaneel';
import type { CodeLabel } from '../services/caseRegistratieApi';
import {
  deel5Verplicht,
  dienVerslagIn,
  type VerslagContext,
  type VerslagInvoer,
  type VerslagResultaat,
} from '../services/gespreksverslagApi';

/**
 * Gespreksverslag-engine (migratie 176). Eén formulier voor alle disciplines;
 * de server bepaalt het sjabloon (A/B/C) en controleert alles opnieuw.
 *
 *  Stap 0   nieuw incident / vervolg + sessienummer (cadans 1-3-5)
 *  Stap 1A  locatie → afdeling
 *  1B–3     A/B: aard, rolverhouding, Vak B, interventies — C: doelen (WKR)
 *  Stap 4   Vak A: alleen voor de medewerker
 *  Deel 5   management-update (A/B bij 1, 3, 5+) — Deel 5B: verlenging op het plafond
 */

interface Props {
  context: VerslagContext;
  /** Route 1/2 */
  afspraakId?: string;
  /** Route 3: wordt pas bij het indienen gelezen; ontbreekt er iets, dan blokkeert de knop. */
  adhoc?: VerslagInvoer['adhoc'] | null;
  onSuccess: (r: VerslagResultaat) => void;
  onCancel?: () => void;
}

const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis');
const wissel = (lijst: string[], code: string) => (lijst.includes(code) ? lijst.filter((c) => c !== code) : [...lijst, code]);
const volgLabel = (n: number) => (n === 1 ? '1e gesprek (intake)' : n >= 6 ? `${n}e gesprek (verlengingstraject)` : `${n}e gesprek`);

const Stap: React.FC<{ nummer: string; titel: string; uitleg?: string; children: React.ReactNode }> = ({ nummer, titel, uitleg, children }) => (
  <section className="border-t border-gray-200 pt-5 space-y-3">
    <div>
      <h3 className="font-semibold text-gray-900">
        <span className="text-gray-400 mr-2">{nummer}</span>
        {titel}
      </h3>
      {uitleg && <p className="text-sm text-gray-600 mt-0.5">{uitleg}</p>}
    </div>
    {children}
  </section>
);

const Vinkjes: React.FC<{ codes: CodeLabel[]; gekozen: string[]; onWissel: (code: string) => void }> = ({ codes, gekozen, onWissel }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
    {codes.map((c) => (
      <label key={c.code} className="flex items-start gap-2 rounded-md border border-gray-200 p-2 text-sm hover:bg-gray-50">
        <input type="checkbox" className="mt-0.5 rounded border-gray-300" checked={gekozen.includes(c.code)} onChange={() => onWissel(c.code)} />
        <span>
          <span className="font-medium text-gray-900">{c.label}</span>
          {c.omschrijving && <span className="block text-xs text-gray-500">{c.omschrijving}</span>}
        </span>
      </label>
    ))}
  </div>
);

export const GespreksverslagEngineForm: React.FC<Props> = ({ context: ctx, afspraakId, adhoc, onSuccess, onCancel }) => {
  const s = ctx.sjabloon;
  const ab = s !== 'C';
  const [isNieuw, setIsNieuw] = useState(ctx.prefill.is_new_incident);
  const [caseId, setCaseId] = useState(ctx.prefill.case_id ?? '');
  const [volgnummer, setVolgnummer] = useState(ctx.prefill.session_sequence_number);
  const [locatie, setLocatie] = useState(ctx.prefill.location_id ?? '');
  const [afdeling, setAfdeling] = useState(ctx.prefill.department_id ?? '');
  const [categorieen, setCategorieen] = useState<string[]>([]);
  const [rol, setRol] = useState(ctx.codes.rol_standaard ?? '');
  const [geslachtVeroorzaker, setGeslachtVeroorzaker] = useState('');
  const [interventies, setInterventies] = useState<string[]>([]);
  const [doelen, setDoelen] = useState<string[]>([]);
  const [vakB, setVakB] = useState('');
  const [vakA, setVakA] = useState('');
  const [arbo, setArbo] = useState('');
  const [fte, setFte] = useState('');
  const [verzuim, setVerzuim] = useState('');
  const [advies, setAdvies] = useState('');
  const [verlenging, setVerlenging] = useState('');
  // Verhoogd bij een AI-concept: de editors nemen externe inhoud alleen bij (her)mount over.
  const [conceptVersie, setConceptVersie] = useState(0);

  const gekozenCase = ctx.lopende_cases.find((c) => c.case_id === caseId);
  // Het volgnummer dat uit de registraties volgt; de server weigert een ander nummer.
  const verwacht = ab ? (isNieuw ? 1 : gekozenCase ? gekozenCase.gesprekken + 1 : null) : ctx.prefill.session_sequence_number;
  const toonDeel5 = deel5Verplicht(s, volgnummer);
  const venster = !!ctx.plafond?.verlenging_venster;
  const afdelingen = ctx.structuur.afdelingen.filter((a) => !locatie || !a.location_id || a.location_id === locatie);
  const conflict = categorieen.includes('arbeidsconflict');

  const kiesCase = (id: string) => {
    setCaseId(id);
    const c = ctx.lopende_cases.find((x) => x.case_id === id);
    if (c) setVolgnummer(c.gesprekken + 1);
  };
  const kiesNieuw = (nieuw: boolean) => {
    setIsNieuw(nieuw);
    if (nieuw) setVolgnummer(1);
    else if (gekozenCase) setVolgnummer(gekozenCase.gesprekken + 1);
    else setVolgnummer(2);
  };
  const wisselCategorie = (code: string) => {
    const nieuw = wissel(categorieen, code);
    setCategorieen(nieuw);
    // BMW-grendel: zonder arbeidsconflict terug naar 'luisterend oor'.
    if (s === 'B' && !nieuw.includes('arbeidsconflict') && ctx.codes.rol_standaard) setRol(ctx.codes.rol_standaard);
  };

  const ontbreekt = useMemo(() => {
    const m: string[] = [];
    if (adhoc === null) m.push('gegevens van het ad-hoc gesprek');
    if (ab && !isNieuw && !caseId) m.push('lopende casus');
    if (verwacht !== null && volgnummer !== verwacht) m.push(`sessienummer (${volgLabel(verwacht)})`);
    if (isNieuw && ctx.structuur.afdelingen.length > 0 && !afdeling) m.push('afdeling');
    if (ab && isNieuw && categorieen.length === 0) m.push('aard van het incident');
    if (ab && isNieuw && !rol) m.push('rolverhouding');
    if (ab && !isNieuw && interventies.length === 0) m.push('interventie');
    if (!ab && doelen.length === 0) m.push('doel van de sessie');
    if (toonDeel5 && !arbo) m.push('arbo-kader');
    if (toonDeel5 && ctx.fte_toegestaan && (!fte || !verzuim)) m.push('FTE-belastbaarheid en verzuimduur');
    if (toonDeel5 && !advies.trim()) m.push('werkvloeradvies');
    if (venster && verlenging === '') m.push('verlengingskeuze');
    return m;
  }, [adhoc, ab, isNieuw, caseId, verwacht, volgnummer, afdeling, categorieen, rol, interventies, doelen, toonDeel5, arbo, fte, verzuim, advies, venster, verlenging, ctx]);

  const indienen = useMutation({
    mutationFn: () =>
      dienVerslagIn({
        ...(afspraakId ? { afspraak_id: afspraakId } : { adhoc: adhoc! }),
        is_new_incident: isNieuw,
        case_id: ab && !isNieuw ? caseId : null,
        session_sequence_number: volgnummer,
        location_id: isNieuw ? locatie || null : null,
        department_id: isNieuw ? afdeling || null : null,
        ...(ab
          ? {
              categorieen: isNieuw ? categorieen : [],
              rolverhouding: isNieuw ? rol || null : null,
              geslacht_veroorzaker: isNieuw ? geslachtVeroorzaker || null : null,
              interventies,
              employer_facing_anonymized_summary: vakB || null,
            }
          : { doelen }),
        client_facing_notes: vakA || null,
        deel5: toonDeel5
          ? {
              arbo_preventive_purpose_enum: arbo,
              werkvloer_advies: advies.trim(),
              ...(ctx.fte_toegestaan ? { fte_belastbaarheid: Number(fte), verwachte_verzuimreductie: verzuim } : {}),
            }
          : null,
        verlenging_extra_sessies: venster ? Number(verlenging) : null,
      }),
    onSuccess,
    onError: foutmelding,
  });

  const vervolgOpties = [2, 3, 4, 5];
  const geblokkeerd = !!ctx.plafond?.voorbij_plafond;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Gespreksverslag</h2>
          <p className="text-sm text-gray-600">
            Sjabloon {s} · {ctx.sjabloon_label}
            {ctx.registratienummer && <> · registratienummer <span className="font-mono">{ctx.registratienummer}</span></>}
          </p>
        </div>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
          {ctx.route === 'ZELFBOEKING' ? 'Route 1 · zelfboeking' : ctx.route === 'PROFESSIONAL_BOEKING' ? 'Route 2 · ingepland door professional' : 'Route 3 · ad-hoc werkvloermelding'}
        </span>
      </div>

      {geblokkeerd && (
        <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          Het sessieplafond van dit traject is bereikt. Een nieuw verslag kan pas na akkoord van HR op de verlenging.
        </div>
      )}
      {ctx.plafond?.verlenging_status === 'PENDING_HR_AUTHORIZATION' && !geblokkeerd && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          Er staat een verlengingsverzoek open bij HR. Nieuwe boekingen in dit traject zijn geblokkeerd tot het akkoord.
        </div>
      )}

      {/* Stap 0 */}
      <Stap nummer="0" titel="Casus & sessienummer" uitleg="Bepaalt de cadans: bij gesprek 1, 3 en 5 of hoger hoort een management-update (Deel 5).">
        {ab ? (
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" checked={isNieuw} onChange={() => kiesNieuw(true)} /> Nieuw incident
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={!isNieuw} onChange={() => kiesNieuw(false)} disabled={ctx.lopende_cases.length === 0} /> Vervolggesprek
              {ctx.lopende_cases.length === 0 && <span className="text-xs text-gray-500">(geen lopende casussen)</span>}
            </label>
          </div>
        ) : (
          <p className="text-sm text-gray-700">{isNieuw ? 'Nieuw traject' : 'Vervolggesprek in dit traject'}</p>
        )}
        {ab && !isNieuw && (
          <label className="block text-sm">
            <span className="text-gray-700">Lopende casus</span>
            <select className="mt-1 w-full max-w-md rounded-md border-gray-300" value={caseId} onChange={(e) => kiesCase(e.target.value)}>
              <option value="">Kies…</option>
              {ctx.lopende_cases.map((c) => (
                <option key={c.case_id} value={c.case_id}>
                  {c.case_code} · gestart {new Date(c.gestart_op).toLocaleDateString('nl-NL')} · {c.gesprekken} {c.gesprekken === 1 ? 'gesprek' : 'gesprekken'}
                </option>
              ))}
            </select>
          </label>
        )}
        <fieldset className="space-y-1 text-sm">
          <legend className="text-gray-700 mb-1">Welk gesprek is dit?</legend>
          {[1, ...vervolgOpties].map((n) => (
            <label key={n} className={`flex items-center gap-2 ${n === 1 ? (isNieuw ? '' : 'opacity-50') : isNieuw ? 'opacity-50' : ''}`}>
              <input type="radio" checked={volgnummer === n} disabled={n === 1 ? !isNieuw : isNieuw} onChange={() => setVolgnummer(n)} />
              {volgLabel(n)}
              <span className="text-xs text-gray-500">{deel5Verplicht(s, n) ? '— met management-update (Deel 5)' : ab ? '— uren-only, geen B2B-rapport' : ''}</span>
            </label>
          ))}
          <label className={`flex items-center gap-2 ${isNieuw ? 'opacity-50' : ''}`}>
            <input type="radio" checked={volgnummer >= 6} disabled={isNieuw} onChange={() => setVolgnummer(Math.max(6, verwacht ?? 6))} />
            6e of hoger (verlengingstraject)
            {volgnummer >= 6 && (
              <input
                type="number"
                min={6}
                max={99}
                className="w-20 rounded-md border-gray-300 py-0.5 text-sm"
                value={volgnummer}
                onChange={(e) => setVolgnummer(Math.max(6, Number(e.target.value) || 6))}
              />
            )}
          </label>
          {verwacht !== null && volgnummer !== verwacht && (
            <p className="text-xs text-red-700">Volgens de registraties is dit het {volgLabel(verwacht)}.</p>
          )}
        </fieldset>
      </Stap>

      {/* Stap 1A */}
      {isNieuw && ctx.structuur.locaties.length + ctx.structuur.afdelingen.length > 0 && (
        <Stap nummer="1A" titel="Organisatie-onderdeel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <label className="block">
              <span className="text-gray-700">Locatie / vestiging</span>
              <select className="mt-1 w-full rounded-md border-gray-300" value={locatie} onChange={(e) => { setLocatie(e.target.value); setAfdeling(''); }}>
                <option value="">Kies…</option>
                {ctx.structuur.locaties.map((l) => <option key={l.id} value={l.id}>{l.naam}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-gray-700">Afdeling</span>
              <select className="mt-1 w-full rounded-md border-gray-300" value={afdeling} onChange={(e) => setAfdeling(e.target.value)}>
                <option value="">Kies…</option>
                {afdelingen.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
              </select>
            </label>
          </div>
        </Stap>
      )}

      {/* Stap 1B–3 */}
      {ab ? (
        <>
          {isNieuw && (
            <>
              <Stap nummer="1B" titel={s === 'A' ? 'Aard van het incident' : 'Aard van de problematiek'} uitleg="Meerdere vinkjes toegestaan bij overlap; elk kenmerk telt mee in het jaarverslag.">
                <Vinkjes codes={ctx.codes.categorieen} gekozen={categorieen} onWissel={wisselCategorie} />
              </Stap>
              <Stap
                nummer="1C"
                titel="Hiërarchische rolverhouding"
                uitleg={s === 'B' ? "Staat op 'luisterend oor'. Alleen aan te passen als u 'Arbeidsconflict' heeft aangevinkt." : 'Verplichte keuze; telt mee in de conflictmatrix.'}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                  {ctx.codes.rolverhoudingen.map((r) => {
                    const uit = s === 'B' && !conflict && r.code !== ctx.codes.rol_standaard;
                    return (
                      <label key={r.code} className={`flex items-start gap-2 ${uit ? 'opacity-40' : ''}`} title={r.omschrijving}>
                        <input type="radio" className="mt-0.5" checked={rol === r.code} disabled={uit} onChange={() => setRol(r.code)} />
                        {r.label}
                      </label>
                    );
                  })}
                </div>
                {ctx.codes.geslachten_veroorzaker.length > 0 && (
                  <fieldset className="mt-3">
                    <legend className="text-sm font-medium text-gray-800">
                      Geslacht beschuldigde / lastveroorzaker <span className="font-normal text-gray-500">(optioneel; telt mee in H4.5)</span>
                    </legend>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {ctx.codes.geslachten_veroorzaker.map((g) => (
                        <label key={g.code} className="flex items-center gap-1.5" title={g.omschrijving || undefined}>
                          <input type="radio" checked={geslachtVeroorzaker === g.code} onChange={() => setGeslachtVeroorzaker(g.code)} />
                          {g.label}
                        </label>
                      ))}
                      {geslachtVeroorzaker && (
                        <button type="button" className="text-xs text-gray-500 underline" onClick={() => setGeslachtVeroorzaker('')}>
                          Wissen
                        </button>
                      )}
                    </div>
                  </fieldset>
                )}
              </Stap>
            </>
          )}
          <Stap nummer="2" titel="Kwalitatieve organisatie-update (Vak B)" uitleg="Geen namen, geen medische gegevens. Alleen culturele patronen en werkvloersignalen. Gaat pas na uw audit naar hoofdstuk 5 van het jaarverslag.">
            <RichTextEditor key={`vakB-${conceptVersie}`} simple content={vakB} onChange={setVakB} minHeight={120} placeholder="Welke patronen ziet u op de werkvloer?" />
          </Stap>
          <Stap nummer="3" titel="Toegepaste interventie & opvolging" uitleg={isNieuw ? undefined : 'Minimaal één; telt cumulatief over de hele casus.'}>
            <Vinkjes codes={ctx.codes.interventies} gekozen={interventies} onWissel={(c) => setInterventies(wissel(interventies, c))} />
          </Stap>
        </>
      ) : (
        <Stap nummer="1B" titel="Doel van de vitaliteitssessie" uitleg="Onderbouwing voor de WKR / arbovrijstelling. Er gaat geen verslag naar de werkgever.">
          <Vinkjes codes={ctx.codes.doelen} gekozen={doelen} onWissel={(c) => setDoelen(wissel(doelen, c))} />
        </Stap>
      )}

      <GespreksverslagAiPaneel
        sjabloon={s}
        deel5={toonDeel5}
        afdelingen={ctx.structuur.afdelingen.map((a) => a.naam)}
        locaties={ctx.structuur.locaties.map((l) => l.naam)}
        onConcept={(c) => {
          // Eén vraag vooraf als er al tekst staat; nooit stilzwijgend overschrijven.
          const heeftTekst = [vakA, ab ? vakB : '', toonDeel5 ? advies : ''].some((t) => t.replace(/<[^>]*>/g, '').trim());
          if (heeftTekst && !window.confirm('Bestaande tekst in Vak A, Vak B en het werkvloeradvies vervangen door het AI-concept?')) return;
          if (c.vak_a) setVakA(c.vak_a);
          if (ab && c.vak_b) setVakB(c.vak_b);
          if (toonDeel5 && c.werkvloer_advies) setAdvies(c.werkvloer_advies);
          setConceptVersie((v) => v + 1);
        }}
      />

      {/* Stap 4 */}
      <Stap nummer="4" titel="Persoonlijke reflectie & huiswerk (Vak A)">
        <div className="flex gap-2 rounded-md bg-green-50 border border-green-200 p-2 text-xs text-green-900">
          <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden />
          {ctx.medewerker_portaal
            ? 'Alleen zichtbaar voor de medewerker in het beveiligde portaal. De werkgever of HR ziet hier nooit iets van.'
            : 'Nooit zichtbaar voor de werkgever. Deze medewerker heeft geen portaalaccount; de tekst blijft dan alleen bij u.'}
        </div>
        <RichTextEditor key={`vakA-${conceptVersie}`} simple content={vakA} onChange={setVakA} minHeight={120} placeholder="Afspraken en huiswerk voor de medewerker…" />
      </Stap>

      {/* Deel 5 */}
      {toonDeel5 && (
        <Stap nummer="5" titel="Management-update & fiscale arbo-verantwoording" uitleg="Vrij van medische en privégegevens. HR ziet dit pas nadat de medewerker toestemming geeft.">
          <fieldset className="space-y-1 text-sm">
            <legend className="text-gray-700 mb-1">Binnen welk wettelijk arbo-kader vallen deze uren?</legend>
            {ctx.codes.arbo_kaders.map((k) => (
              <label key={k.code} className="flex items-start gap-2">
                <input type="radio" className="mt-0.5" checked={arbo === k.code} onChange={() => setArbo(k.code)} />
                <span>{k.label} <span className="text-xs text-gray-500">({k.omschrijving})</span></span>
              </label>
            ))}
          </fieldset>
          {ctx.fte_toegestaan && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <label className="block">
                <span className="text-gray-700">Huidige FTE-belastbaarheid</span>
                <select className="mt-1 w-full rounded-md border-gray-300" value={fte} onChange={(e) => setFte(e.target.value)}>
                  <option value="">Kies…</option>
                  {ctx.codes.fte_opties.map((o) => <option key={o} value={o}>{o}%{o === 100 ? ' (volledig inzetbaar)' : ''}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-gray-700">Verwachte verzuimduur / reductie</span>
                <select className="mt-1 w-full rounded-md border-gray-300" value={verzuim} onChange={(e) => setVerzuim(e.target.value)}>
                  <option value="">Kies…</option>
                  {ctx.codes.verzuim_opties.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </label>
            </div>
          )}
          <label className="block text-sm">
            <span className="text-gray-700">Praktisch werkvloeradvies voor HR / leidinggevende</span>
            <textarea
              className="mt-1 w-full rounded-md border-gray-300"
              rows={3}
              maxLength={5000}
              value={advies}
              onChange={(e) => setAdvies(e.target.value)}
              placeholder="Uitsluitend functioneel en organisatorisch advies, bijv. ‘Tijdelijk geen nachtdiensten adviseren wegens slaapfrictie’."
            />
          </label>
        </Stap>
      )}

      {/* Deel 5B */}
      {venster && (
        <Stap nummer="5B" titel="Budget-verlengingsgrendel">
          <p className="text-sm text-gray-800">
            U heeft de maximale budgettaire limiet van dit traject bereikt ({ctx.plafond!.max_sessies} sessies). Hoeveel extra sessies acht u professioneel noodzakelijk
            om deze casus duurzaam en veilig af te ronden?
          </p>
          <select className="w-full max-w-sm rounded-md border-gray-300 text-sm" value={verlenging} onChange={(e) => setVerlenging(e.target.value)}>
            <option value="">Kies…</option>
            {ctx.codes.verlenging_opties.map((o) => (
              <option key={o} value={o}>{o === 0 ? 'Geen verlenging nodig — traject afronden' : `+${o} extra sessies${o === 10 ? ' (spoor 2)' : ''}`}</option>
            ))}
          </select>
          {Number(verlenging) > 0 && (
            <p className="text-xs text-amber-800">
              Na het opslaan gaat het verzoek naar HR (anoniem, alleen met locatie/afdeling). De agenda blokkeert nieuwe boekingen in dit traject tot het akkoord.
            </p>
          )}
        </Stap>
      )}

      <div className="border-t border-gray-200 pt-4 flex flex-wrap items-center justify-end gap-3">
        {ontbreekt.length > 0 && <p className="text-xs text-gray-500 mr-auto">Nog in te vullen: {ontbreekt.join(', ')}.</p>}
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Annuleren</Button>}
        <Button type="button" disabled={ontbreekt.length > 0 || geblokkeerd} loading={indienen.isPending} onClick={() => indienen.mutate()}>
          <FileText className="w-4 h-4 mr-2" />
          Verslag opslaan
        </Button>
      </div>
    </div>
  );
};
